// Utility functions for profile management

/**
 * Get the display name for a user profile
 * @param {Object} profile - User profile object
 * @returns {string} The display name to show to other users
 */
export const getDisplayName = (profile) => {
  if (!profile) return 'Unknown User';
  
  // Handle old data structure (from participants table)
  if (profile.user_name) {
    return profile.user_name;
  }
  
  // If user has a custom display name and doesn't use generated alias
  if (profile.display_name && !profile.use_generated_alias) {
    return profile.display_name;
  }
  
  // If user uses generated alias
  if (profile.use_generated_alias && profile.generated_alias) {
    return profile.generated_alias;
  }
  
  // Fallback to old name field or generated alias
  if (profile.name) {
    return profile.name;
  }
  
  // Generate a fallback alias if nothing is available
  return generateFallbackAlias(profile.user_id || profile.id);
};

/**
 * Generate a fallback alias for users without one
 * @param {string} userId - User ID to generate consistent alias
 * @returns {string} Generated alias
 */
const generateFallbackAlias = (userId) => {
  const adjectives = ['Swift', 'Bright', 'Calm', 'Bold', 'Wise', 'Quick', 'Smart', 'Brave', 'Kind', 'Warm'];
  const nouns = ['Traveler', 'Explorer', 'Pilot', 'Navigator', 'Captain', 'Guide', 'Rider', 'Driver', 'Passenger', 'Companion'];
  
  // Use user ID to generate consistent alias
  const hash = userId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  const adjective = adjectives[Math.abs(hash) % adjectives.length];
  const noun = nouns[Math.abs(hash >> 8) % nouns.length];
  const number = Math.abs(hash >> 16) % 999 + 1;
  
  return `${adjective}${noun}${number}`;
};

/**
 * Check if a display name is appropriate
 * @param {string} displayName - Display name to check
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim().length < 2) {
    return { isValid: false, error: 'Display name must be at least 2 characters long' };
  }
  
  if (displayName.trim().length > 10) {
    return { isValid: false, error: 'Display name must be 10 characters or less' };
  }
  
  // Check for inappropriate content
  const inappropriateWords = [
    'admin', 'moderator', 'support', 'help', 'test', 'fake', 'spam',
    'official', 'staff', 'team', 'system', 'bot', 'automated'
  ];
  
  const lowerDisplayName = displayName.toLowerCase();
  const hasInappropriateContent = inappropriateWords.some(word => 
    lowerDisplayName.includes(word)
  );
  
  if (hasInappropriateContent) {
    return { isValid: false, error: 'Display name contains inappropriate content' };
  }
  
  return { isValid: true, error: null };
};

/**
 * Generate a new random alias
 * @returns {string} Generated alias
 */
export const generateNewAlias = () => {
  const adjectives = ['Swift', 'Bright', 'Calm', 'Bold', 'Wise', 'Quick', 'Smart', 'Brave', 'Kind', 'Warm'];
  const nouns = ['Traveler', 'Explorer', 'Pilot', 'Navigator', 'Captain', 'Guide', 'Rider', 'Driver', 'Passenger', 'Companion'];
  
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 999) + 1;
  
  return `${adjective}${noun}${number}`;
}; 